import { useState, useCallback, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_PRESETS = [
  { value: "today",      labelKey: "relatorios.today",     days: 0 },
  { value: "7d",         labelKey: "relatorios.last7",      days: 7 },
  { value: "30d",        labelKey: "relatorios.last30",     days: 30 },
  { value: "this_month", labelKey: "relatorios.thisMonth",  days: null },
  { value: "last_month", labelKey: "relatorios.lastMonth",  days: null },
  { value: "quarter",    labelKey: "relatorios.quarter",    days: null },
  { value: "this_year",  labelKey: "relatorios.thisYear",   days: null },
  { value: "custom",     labelKey: "relatorios.custom",     days: null },
];

function getPresetDates(preset) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  switch (preset) {
    case "today": return { start: iso(now), end: iso(now) };
    case "7d": { const s = new Date(now); s.setDate(s.getDate()-7); return { start: iso(s), end: iso(now) }; }
    case "30d": { const s = new Date(now); s.setDate(s.getDate()-30); return { start: iso(s), end: iso(now) }; }
    case "this_month": return { start: `${now.getFullYear()}-${pad(now.getMonth()+1)}-01`, end: iso(now) };
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth()-1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: iso(s), end: iso(e) };
    }
    case "quarter": {
      const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth()/3)*3, 1);
      return { start: iso(qStart), end: iso(now) };
    }
    case "this_year": return { start: `${now.getFullYear()}-01-01`, end: iso(now) };
    default: return { start: iso(new Date(now.getFullYear(), 0, 1)), end: iso(now) };
  }
}

export { PERIOD_PRESETS, getPresetDates };

export function useReportFilters(reportKey) {
  const storageKey = `pipe4_report_filters_${reportKey}`;

  const getDefaultFilters = useCallback(() => {
    const preset = "this_month";
    const dates = getPresetDates(preset);
    return {
      pipelineIds: [],
      responsavelIds: [],
      status: [],
      etapaIds: [],
      periodPreset: preset,
      periodStart: dates.start,
      periodEnd: dates.end,
      periodField: "created_at",
      advancedFilters: [],
      advancedLogic: "AND",
    };
  }, []);

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return { ...getDefaultFilters(), ...JSON.parse(saved) };
    } catch {}
    return getDefaultFilters();
  });

  const [pipelines, setPipelines] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, rRes] = await Promise.all([
          axios.get(`${API}/pipelines`, { withCredentials: true }),
          axios.get(`${API}/users/responsaveis`, { withCredentials: true }),
        ]);
        setPipelines(pRes.data);
        setResponsaveis(rRes.data);
      } catch {}
    };
    load();
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [storageKey]);

  const updatePeriodPreset = useCallback((preset) => {
    if (preset !== "custom") {
      const dates = getPresetDates(preset);
      setFilters(prev => {
        const next = { ...prev, periodPreset: preset, periodStart: dates.start, periodEnd: dates.end };
        try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
        return next;
      });
    } else {
      updateFilter("periodPreset", "custom");
    }
  }, [storageKey, updateFilter]);

  const resetFilters = useCallback(() => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    try { localStorage.removeItem(storageKey); } catch {}
  }, [storageKey, getDefaultFilters]);

  // Build API filter object
  const buildApiFilters = useCallback((overrides = {}) => {
    return {
      pipeline_ids: filters.pipelineIds.length ? filters.pipelineIds : pipelines.map(p => p.id),
      responsavel_ids: filters.responsavelIds,
      status: filters.status,
      etapa_ids: filters.etapaIds,
      period: {
        start: filters.periodStart + "T00:00:00Z",
        end: filters.periodEnd + "T23:59:59Z",
        field: filters.periodField,
      },
      ...overrides,
    };
  }, [filters, pipelines]);

  // Active filter chips for display
  const activeChips = [];
  if (filters.pipelineIds.length) activeChips.push({ key: "pipelineIds", label: `${filters.pipelineIds.length} pipeline(s)` });
  if (filters.responsavelIds.length) activeChips.push({ key: "responsavelIds", label: `${filters.responsavelIds.length} responsável(is)` });
  if (filters.status.length) activeChips.push({ key: "status", label: filters.status.join(", ") });
  if (filters.advancedFilters.length) activeChips.push({ key: "advancedFilters", label: `${filters.advancedFilters.length} filtro(s) avançado(s)` });

  return {
    filters, updateFilter, updatePeriodPreset, resetFilters,
    buildApiFilters, pipelines, responsaveis, activeChips,
    PERIOD_PRESETS,
  };
}
